import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Award, Download, Sparkles } from 'lucide-react';
import { publicCertificateFileUrl, useApi, type EarnYourOwn } from '@/lib/api';
import { formatMinutes } from '@/components/catalog';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui';
import { LinkedInActions } from '@/components/certificate-download';
import { formatCertificateDate, formatDateTime } from '@/lib/dates';
import { OfferedBy } from '@/components/org-brand';

const secondaryLink =
  'flex items-center justify-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted';

/**
 * Public certificate verification, for anyone checking a certificate. Visitors
 * can download a copy for their records. The signed-in owner also gets the
 * LinkedIn actions (as on Your certificates); nobody else does, so no one
 * can add another person's certificate to their profile. Everyone else is
 * invited to earn their own (`EarnYourOwnCard`).
 */
export function VerifyCertificatePage() {
  const { code = '' } = useParams();
  const api = useApi();
  const cert = useQuery({
    queryKey: ['verify', code],
    queryFn: () => api.verifyCertificate(code),
    retry: false,
  });
  const { user } = useAuth();
  const mine = useQuery({
    queryKey: ['certificates'],
    queryFn: api.certificates,
    enabled: !!user,
  });
  const isOwner = !!mine.data?.certificates.some((m) => m.code === code.toUpperCase());

  if (cert.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (cert.isError) {
    return (
      <Card className="text-center">
        <p className="font-medium">Certificate not found</p>
        <p className="mt-1 text-sm text-muted-foreground">
          It may be private, or the code may be wrong.
        </p>
      </Card>
    );
  }

  const c = cert.data;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <Card className="text-center">
        <Award className="mx-auto size-10 text-primary" aria-hidden />
        <p className="mt-3 text-sm uppercase tracking-wide text-muted-foreground">
          {c.kind === 'event' ? 'Certificate of participation' : 'Certificate of achievement'}
        </p>
        <h1 className="mt-2 text-2xl font-semibold">{c.title}</h1>
        <p className="mt-4">
          Awarded to <span className="font-semibold">{c.recipientName}</span>
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {c.offeredBy ? 'Issued' : `by ${c.issuerName} ·`}{' '}
          {formatCertificateDate(c.issuedAt, c.timeZone)}
        </p>
        {c.offeredBy && <OfferedBy org={c.offeredBy} className="mt-3 justify-center" />}
        <p className="mt-4 text-xs text-muted-foreground">
          Verified by Nirvik Ready · ID {c.code} · Issued {formatDateTime(c.issuedAt)} your time
        </p>
      </Card>
      <div className="space-y-4">
        {isOwner ? <LinkedInActions cert={c} kind={c.kind} /> : <EarnYourOwnCard earn={c.earn} />}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
          <a href={publicCertificateFileUrl(c.code, 'pdf')} download className={secondaryLink}>
            <Download className="size-4" aria-hidden /> Download PDF
          </a>
          <a href={publicCertificateFileUrl(c.code, 'png')} download className={secondaryLink}>
            <Download className="size-4" aria-hidden /> Download PNG
          </a>
        </div>
      </div>
    </div>
  );
}

const primaryLink =
  'inline-flex w-full items-center justify-center gap-2 rounded-[var(--radius)] bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90';

/** The invitation, by what the certificate was earned on; a starting point when that isn't open to all. */
function invitation(earn: EarnYourOwn | null) {
  const time = earn?.minutes ? `, about ${formatMinutes(earn.minutes)}` : '';
  switch (earn?.kind) {
    case 'course':
      return {
        heading: 'Earn this one too',
        text: `“${earn.title}” is free and self-paced${time}. Finish it and the certificate is yours.`,
        cta: 'Start the course, free',
        to: `/c/${earn.slug}`,
      };
    case 'quiz':
      return {
        heading: 'Think you’d pass?',
        text: `Take “${earn.title}”, free${time}. Pass it and you get a certificate like this one.`,
        cta: 'Take the challenge',
        to: `/q/${earn.slug}`,
      };
    case 'event':
      return {
        heading: 'Join the next one',
        text: `“${earn.title}” is free to join. Take part and you get a certificate like this one.`,
        cta: 'Save my seat',
        to: `/e/${earn.slug}`,
      };
    default:
      return {
        heading: 'Get your own certificate',
        text: 'Free courses and quick challenges in cyber and AI safety, each with a certificate. Two minutes finds your starting point.',
        cta: 'Find my starting point',
        to: '/paths/start',
      };
  }
}

/** For visitors: earn a certificate like the one they're looking at. */
function EarnYourOwnCard({ earn }: { earn: EarnYourOwn | null }) {
  const i = invitation(earn);
  return (
    <section className="rounded-[var(--radius)] border-2 border-primary/60 bg-primary/10 p-5">
      <p className="flex items-center gap-2 text-xs font-semibold tracking-wider text-primary uppercase">
        <Sparkles className="size-4" aria-hidden /> Your turn
      </p>
      <h2 className="mt-2 text-lg font-semibold">{i.heading}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{i.text}</p>
      <Link to={i.to} className={`mt-4 ${primaryLink}`}>
        {i.cta} <ArrowRight className="size-4" aria-hidden />
      </Link>
      {earn && (
        <Link
          to="/paths/start"
          className="-my-2 mt-3 block py-2 text-center text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Or find your own starting point
        </Link>
      )}
    </section>
  );
}
